import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
	API_URL,
	COMPANION_CODE_STORAGE_KEY,
	FIREBASE_AUTH,
	FIRESTORE,
	LocalStorageService,
	ORGANIZATION_ID,
} from '@context/frontend/common';
import { isExpired } from '@context/shared/types/common';
import { GuestSession, Organization } from '@context/shared/types/organization';
import {
	User as FirebaseUser,
	onAuthStateChanged,
	signInAnonymously,
	signOut,
} from 'firebase/auth';
import {
	collection,
	doc,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	onSnapshot,
} from 'firebase/firestore';
import { firstValueFrom, tap } from 'rxjs';

interface RedeemCompanionCodeResponse {
	orgId: string;
	expiresAt: { _seconds: number; _nanoseconds: number };
}

export type SessionInvalidationReason =
	'expired' | 'deleted' | 'non-existent' | 'logout';

/**
 * Service for managing guest authentication via companion codes (anonymous-centric).
 *
 * Flow:
 * 1. Guest submits companion code
 * 2. Client signs in with Firebase Anonymous Auth (signInAnonymously)
 * 3. Client calls backend to redeem code; backend binds anonymous UID to guest session
 * 4. Client starts listener on guest session document
 * 5. If session expires (monthly), is deleted, or doesn't exist, sign out and route to expired
 *
 * Note: Sessions expire monthly (30 days) and require re-authentication.
 */
@Injectable({ providedIn: 'root' })
export class GuestAuthService {
	private readonly auth = inject(FIREBASE_AUTH);
	private readonly firestore = inject(FIRESTORE);
	private readonly http = inject(HttpClient);
	private readonly router = inject(Router);
	private readonly apiUrl = inject(API_URL);
	private readonly organizationId$ = inject(ORGANIZATION_ID);

	readonly firebaseUser = signal<FirebaseUser | null>(null);
	readonly organization = signal<Organization | null>(null);
	readonly organizationRef = signal<DocumentReference<Organization> | null>(
		null,
	);

	private readonly storageKey = inject(COMPANION_CODE_STORAGE_KEY);
	private readonly localStorage = inject(LocalStorageService);
	private readonly isRedeemed = signal<boolean>(false);

	private sessionListenerUnsubscribe: (() => void) | null = null;

	constructor() {
		onAuthStateChanged(this.auth, this.onAuthStateChanged.bind(this));
	}

	/**
	 * Handles auth state changes (e.g. page refresh, sign in, sign out).
	 * When user is null: resets guest state.
	 * When user is present: restores guest state from token orgId + guestSession if valid.
	 */
	async onAuthStateChanged(firebaseUser: FirebaseUser | null): Promise<void> {
		this.firebaseUser.set(firebaseUser);
		if (!firebaseUser) return this.reset();

		await this.setupGuestSession(firebaseUser.uid);
	}

	/**
	 * Resets guest state (organization, organizationRef, organizationId$, listeners).
	 * Call when user signs out or when guest session is invalid.
	 */
	private reset(): void {
		this.cleanup();
		this.localStorage.removeItem(this.storageKey);
		this.firebaseUser.set(null);
		this.organizationId$.next(null);
		this.organization.set(null);
		this.organizationRef.set(null);
		this.isRedeemed.set(false);
	}

	readonly redeemCompanionCode = (
		code: string,
	): Promise<{ orgId: string }> => {
		const isRedeemed = this.isRedeemed();
		if (isRedeemed) {
			const orgId = this.organizationId$.value;
			if (!orgId) throw Error('Missing Organization ID');
			return Promise.resolve({ orgId });
		}

		return firstValueFrom(
			this.http
				.post<RedeemCompanionCodeResponse>(
					`${this.apiUrl}/v1/organizations/companion-code/redeem`,
					{ code },
				)
				.pipe(
					tap((response) => {
						this.organizationId$.next(response.orgId);
						this.isRedeemed.set(true);
						this.setCode(code);
					}),
				),
		);
	};

	/**
	 * Submits a companion code: signs in anonymously, redeems the code, and binds the session.
	 *
	 * @param input Plaintext companion code
	 * @returns Promise that resolves when sign-in and redeem are complete
	 */
	async submitCompanionCode(input: string): Promise<void> {
		const code = input.toLowerCase().trim();
		if (!code) throw Error('Companion code is required');

		await signInAnonymously(this.auth).catch((error) => {
			console.error('Error signing in anonymously:', error);
			this.cleanup();
			throw error;
		});

		await this.redeemCompanionCode(code).catch((error) => {
			console.error('Error redeeming companion code:', error);
			this.cleanup();
			throw error;
		});
	}

	/**
	 * Loads the organization document and sets AuthService.organization for companion apps.
	 * Used after redeem and can be called when guard runs (e.g. on refresh) if organization is not yet set.
	 */
	async setOrganizationForGuest(orgId: string): Promise<void> {
		const orgRef = doc(
			this.firestore,
			'organizations',
			orgId,
		) as DocumentReference<Organization>;

		const snapshot = await getDoc(orgRef);
		if (snapshot.exists()) {
			this.organizationRef.set(orgRef);
			this.organization.set(snapshot.data() as Organization);
		}
	}

	/**
	 * Starts a realtime listener on the guest session document.
	 * If session expires, is deleted, or doesn't exist, signs out the guest.
	 * Sessions expire monthly (30 days) and require re-authentication.
	 *
	 * Important: Only one listener should be active at a time.
	 * This method handles cleanup of any existing listener.
	 */
	private startSessionListener(): void {
		// Clean up any existing listener
		this.cleanup();

		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) throw new Error('Firebase user is required');

		const orgId = this.organizationId$.value;
		if (!orgId) throw new Error('Organization ID is required');

		const sessionRef = doc(
			this.firestore,
			'organizations',
			orgId,
			'guestSessions',
			firebaseUser.uid,
		) as DocumentReference<GuestSession>;

		// Listen for changes to the organization document
		this.sessionListenerUnsubscribe = onSnapshot(
			sessionRef,
			(snapshot: DocumentSnapshot<GuestSession>) => {
				if (!snapshot.exists()) {
					this.signOut('non-existent');
					return;
				}

				const data = snapshot.data() as GuestSession;
				if (data.deletedAt) {
					this.signOut('deleted');
					return;
				}

				if (isExpired(data.expiresAt)) {
					this.signOut('expired');
					return;
				}
			},
			(error) => {
				console.error('Error listening to guest session:', error);
			},
		);
	}

	/**
	 * Cleans up the organization session listener.
	 * Call this when signing out or when listener is no longer needed.
	 */
	cleanup(): void {
		if (this.sessionListenerUnsubscribe) {
			this.sessionListenerUnsubscribe();
			this.sessionListenerUnsubscribe = null;
		}
	}

	/**
	 * Signs out the guest user and cleans up listeners.
	 * Use this instead of direct signOut() to ensure proper cleanup.
	 */
	readonly signOut = (
		reason: SessionInvalidationReason = 'logout',
	): Promise<void> =>
		signOut(this.auth)
			.catch((error) => {
				console.error('Error signing out:', error);
			})
			.finally(() => {
				this.reset();
				this.router.navigate(['/auth'], { queryParams: { reason } });
			});

	getCode(): string | null {
		return this.localStorage.getItem(this.storageKey);
	}

	setCode(code: string): void {
		this.localStorage.setItem(this.storageKey, code.toLowerCase().trim());
	}

	hasCode(): boolean {
		const code = this.getCode();
		return !!code && code.length > 0;
	}

	/**
	 * Checks whether the guest session for the given org and user is still valid
	 * (document exists and expiresAt is in the future).
	 * Used by the guard to kick out users whose session has expired.
	 * Sessions expire monthly (30 days) and require re-authentication.
	 *
	 * @param orgId Organization ID
	 * @param uid Firebase Auth UID (anonymous user)
	 * @returns true if session exists and is not expired
	 */
	async setupGuestSession(
		uid: string,
		orgId: string | null = null,
	): Promise<boolean> {
		orgId = orgId ?? this.organizationId$.value;
		if (!orgId) return false;

		this.organizationId$.next(orgId);
		const orgRef = doc(this.firestore, 'organizations', orgId);
		const sessionRef = doc(collection(orgRef, 'guestSessions'), uid);

		const snapshot = await getDoc(sessionRef);
		if (!snapshot.exists()) return false;

		const data = snapshot.data() as GuestSession;
		if (!data) return false;

		const isValid = data.expiresAt.toDate() > new Date();
		if (!isValid) {
			this.reset();
			return false;
		}

		const code = this.getCode();
		if (!code) return false;

		await this.redeemCompanionCode(code);
		await this.setOrganizationForGuest(orgId);

		await this.updateCustomClaims();
		await this.setOrganizationForGuest(orgId);

		this.startSessionListener();
		return true;
	}

	/**
	 * Updates custom claims on the Firebase Auth token with orgId and role.
	 * After updating claims, the ID token is refreshed to include the new claims.
	 *
	 * @param orgId Organization ID to set in claims
	 * @param role User role (user, admin, owner, super)
	 */
	async updateCustomClaims(): Promise<string | null> {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) return null;

		const orgId = this.organizationId$.value;
		if (!orgId) return null;

		try {
			// Call backend to set custom claims
			await firstValueFrom(
				this.http.get(`${this.apiUrl}/v1/users/claims/update`),
			);

			// Refresh the ID token to get the new claims
			return this.refreshIdToken();
		} catch (error) {
			// Don't throw - allow app to continue even if claims update fails
			// This is a non-critical operation for initial auth flow
			console.error('AuthService.updateCustomClaims', error);
			return null;
		}
	}

	/**
	 * Refreshes the Firebase Auth ID token to get updated custom claims.
	 * This should be called after updating custom claims on the backend.
	 */
	refreshIdToken(): Promise<string | null> {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) {
			console.warn('AuthService.refreshIdToken: No Firebase user');
			return Promise.resolve(null);
		}

		return firebaseUser.getIdToken(true).catch((error) => {
			console.error('AuthService.refreshIdToken', error);
			throw error;
		});
	}
}
